<template>
  <div class="live-data-page">
    <h2 class="page-title">{{ $t('live_data_plant_list') }}</h2>

    <div v-if="loading" class="loading-state">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
    </div>

    <div v-else-if="plants.length > 0" class="plant-list-card">
      <template v-for="(plant, index) in plants" :key="plant.id">
        <div v-if="index > 0" class="row-separator"></div>
        <PlantListItem
          :plant="plant"
          :is-selected="selectedPlantId === plant.id"
          @select="selectPlant"
        />
        <transition name="slide-down">
          <div v-if="selectedPlantId === plant.id" class="plant-detail-wrapper">
            <PlantDetail :plant-id="plant.id" />
          </div>
        </transition>
      </template>
    </div>

    <p v-else class="empty-state">{{ $t('no_live_data') }}</p>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import apiClient from '@/services/apiClient';
import { createToast } from '@/assets/js/myToast.js';
import PlantListItem from '@/components/livedata/PlantListItem.vue';

const PlantDetail = defineAsyncComponent(() => import('@/components/livedata/PlantDetail.vue'));

export default {
  name: 'LiveData',
  components: { PlantListItem, PlantDetail },
  data() {
    return {
      plants: [],
      selectedPlantId: null,
      loading: false,
    };
  },
  mounted() {
    this.fetchPlantList();
  },
  methods: {
    async fetchPlantList() {
      this.loading = true;
      try {
        const { data } = await apiClient.get('/api/v1/plants/live/');
        this.plants = data;
      } catch (err) {
        createToast({ text: this.$t('error_loading_live_data'), type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    selectPlant(plantId) {
      this.selectedPlantId = this.selectedPlantId === plantId ? null : plantId;
    },
  },
};
</script>

<style scoped>
.live-data-page {
  max-width: 680px;
  margin: 0 auto;
  padding: 24px 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #1d1d1f;
  margin-bottom: 20px;
  letter-spacing: -0.3px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.plant-list-card {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(0, 0, 0, 0.06),
    0 2px 8px rgba(0, 0, 0, 0.04);
}

.row-separator {
  height: 1px;
  background: #e5e5ea;
  margin-left: 16px;
}

.plant-detail-wrapper {
  background: #f5f5f7;
  border-top: 1px solid #e5e5ea;
  padding: 16px;
}

.empty-state {
  color: #6e6e73;
  font-size: 15px;
  text-align: center;
  padding: 48px 0;
}

.slide-down-enter-active {
  transition: all 0.2s ease-out;
}
.slide-down-leave-active {
  transition: all 0.15s ease-in;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
